import { useState, useEffect, useCallback } from 'react';
import { DataStore } from 'aws-amplify';
import { TripSheet } from '../models';
import { TripSheetProps, convertTripSheetToProps } from '../datastore/tripSheets';
import { ObserveTripSheetsMode, projectTripSheetForMode } from './tripSheetProjection';
import { captureError } from '../common/sentry';

interface UseObserveTripSheetsOptions {
  mode?: ObserveTripSheetsMode;
}

function upsertById(items: TripSheetProps[], nextItem: TripSheetProps): TripSheetProps[] {
  const index = items.findIndex((item) => item.id === nextItem.id);

  if (index === -1) {
    return [...items, nextItem];
  }

  const cloned = items.slice();
  cloned[index] = nextItem;
  return cloned;
}

export default function useObserveTripSheets(options?: UseObserveTripSheetsOptions) {
  const [tripSheets, setTripSheets] = useState<TripSheetProps[]>([]);
  const mode: ObserveTripSheetsMode = options?.mode || 'full';

  const projectForAdminPanel = useCallback(
    (tripSheet: TripSheetProps): TripSheetProps => projectTripSheetForMode(tripSheet, mode),
    [mode]
  );

  useEffect(() => {
    const retrieveData = async () => {
      const entities = await DataStore.query(TripSheet);
      console.debug('useObserveTripSheets: fetched', entities.length, 'trip sheets');

      const validTripSheets: TripSheetProps[] = [];
      for (const entity of entities) {
        const converted = convertTripSheetToProps(entity);
        if (converted) {
          validTripSheets.push(projectForAdminPanel(converted));
        }
      }

      setTripSheets(validTripSheets);
    };

    const subscription = DataStore.observe(TripSheet).subscribe({
      next: (message: any) => {
        const opType = message?.opType as 'INSERT' | 'UPDATE' | 'DELETE' | undefined;
        const element = message?.element as TripSheet | undefined;

        if (!opType || !element?.id) {
          return;
        }

        if (opType === 'DELETE') {
          setTripSheets((previous) => previous.filter((item) => item.id !== element.id));
          return;
        }

        const converted = convertTripSheetToProps(element);
        if (!converted) {
          return;
        }

        setTripSheets((previous) => upsertById(previous, projectForAdminPanel(converted)));
      },
      error: (error) => {
        console.error('Error observing TripSheet changes:', error);
        captureError(error, 'useSubscribeTripsheet-observe');
      }
    });

    retrieveData();
    return () => {
      subscription.unsubscribe();
    };
  }, [projectForAdminPanel]);

  return tripSheets;
}
